import { useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { X } from "lucide-react";
import { LearningStep } from "components/member/learningSteps";
import { useTypewriter } from "components/hooks/useTypewriter";
import { useMemberDetails } from "components/context/MemberDetailsContext";
import { useUpdateProductPrompts } from "services/nux";

const ExampleTypewriter = ({ examples }: { examples: string[] }) => {
  const text = useTypewriter({
    texts: examples,
    enabled: true,
    typingSpeed: 30,
    erasingSpeed: 15,
    pauseDuration: 2500,
  });

  return (
    <Box
      sx={{
        minHeight: "48px",
        display: "flex",
        alignItems: "center",
      }}
    >
      <Typography variant="body" sx={{ color: "#344054", fontStyle: "italic" }}>
        &ldquo;{text}&rdquo;
        <Box
          component="span"
          sx={{
            display: "inline-block",
            width: "2px",
            height: "1em",
            backgroundColor: "#198282",
            marginLeft: "1px",
            verticalAlign: "text-bottom",
            animation: "blink 1s step-end infinite",
            "@keyframes blink": {
              "0%, 100%": { opacity: 1 },
              "50%": { opacity: 0 },
            },
          }}
        />
      </Typography>
    </Box>
  );
};

const dismissKey = (step: LearningStep) => `learningStep_${step.key}_dismissed`;

export default ({
  step,
  onDismiss,
}: {
  step: LearningStep;
  onDismiss?: () => void;
}) => {
  const { data } = useMemberDetails();
  const { request: updateProductPrompts } = useUpdateProductPrompts();

  const productPrompts = data?.member?.productPrompts
    ? JSON.parse(data.member.productPrompts)
    : {};
  const [dismissed, setDismissed] = useState(
    productPrompts[dismissKey(step)] === "dismissed",
  );

  const handleDismiss = async () => {
    setDismissed(true);
    onDismiss?.();
    await updateProductPrompts({
      key: dismissKey(step),
      value: "dismissed",
    });
  };

  if (dismissed) return null;

  return (
    <Box
      sx={{
        position: "relative",
        borderRadius: "16px",
        border: "1px solid #EAECF0",
        padding: "24px",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        marginBottom: "24px",
      }}
    >
      <IconButton
        onClick={handleDismiss}
        size="small"
        sx={{ position: "absolute", top: "12px", right: "12px" }}
      >
        <X size={18} />
      </IconButton>
      <Box sx={{ display: "flex", alignItems: "center", gap: "16px" }}>
        <Box
          sx={{
            width: "56px",
            height: "56px",
            borderRadius: "18px",
            backgroundColor: step.accent,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <step.Icon size={28} color="#198282" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <ExampleTypewriter key={step.key} examples={step.examples} />
        </Box>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        <Typography variant="body">{step.description}</Typography>
        <Typography variant="body">{step.helper}</Typography>
      </Box>
    </Box>
  );
};
